import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import ProUpsell from '@/components/ProUpsell';
import Seo from '@/components/Seo';
import { toast, Toaster } from 'sonner';
import { Plus, Trash2, Pencil, X, TrendingUp, Wrench, Truck, PoundSterling, Percent, Receipt, Bell, AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, MapPin, Clock } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';

const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);

const TABS = [
  { key: 'calendar', label: 'Calendar', icon: CalendarDays },
  { key: 'enquiries', label: 'Enquiries', icon: TrendingUp },
  { key: 'tools', label: 'Tools', icon: Wrench },
  { key: 'vans', label: 'Vans', icon: Truck },
  { key: 'tax', label: 'Tax dates', icon: Receipt },
];

export default function BusinessPage() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.role === 'admin';
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'calendar';
  const setTab = (k) => setParams({ tab: k }, { replace: true });

  if (!isPro) return <ProUpsell feature="Business Tracker" testIdSuffix="business" tier="pro" />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Business Tracker" noindex />
      <Toaster position="top-center" richColors />
      <header className="mb-6">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">Business</p>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">Business Tracker</h1>
        <p className="text-slate-600 mt-2">Track enquiries &amp; conversion, your tool inventory, and van running costs — all in one place.</p>
      </header>

      <ReminderBanner />

      <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`biz-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 font-display font-bold uppercase text-sm tracking-wide transition-colors border-b-2 -mb-[2px] ${
                tab === t.key ? 'border-brand text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-700'
              }`}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'calendar' && <CalendarTab />}
      {tab === 'enquiries' && <EnquiriesTab />}
      {tab === 'tools' && <ToolsTab />}
      {tab === 'vans' && <VansTab />}
      {tab === 'tax' && <TaxDatesTab />}
    </div>
  );
}

/* ---------------- Reminders banner ---------------- */
function ReminderBanner() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get('/business/reminders?days=30').then(({ data }) => setItems(data.items || [])).catch(() => {});
  }, []);
  if (items.length === 0) return null;
  const fmt = (d) => (d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? 'due today' : `in ${d}d`);
  return (
    <div data-testid="biz-reminder-banner" className="tm-card p-4 mb-6 border border-brand" style={{ background: 'rgb(var(--tm-brand-rgb) / 0.06)' }}>
      <div className="flex items-center gap-2 mb-2">
        <Bell size={16} className="text-brand" />
        <h3 className="font-display font-bold uppercase">Coming up — {items.length} due within 30 days</h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((i) => {
          const urgent = i.days_left < 7;
          return (
            <span key={i.id} data-testid={`biz-reminder-${i.id}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono border ${urgent ? 'border-rose-500 text-rose-700 bg-rose-50' : 'border-amber-500 text-amber-700 bg-amber-50'}`}>
              {urgent && <AlertTriangle size={12} />}
              <b className="font-display uppercase">{i.type}</b>
              <span>{i.label.replace(/^.*? — /, '')}</span>
              <span className="opacity-70">· {i.due_date} · {fmt(i.days_left)}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="tm-card p-4">
      <div className="flex items-center gap-2 text-slate-500"><Icon size={15} className="text-brand" /><span className="text-[10px] uppercase tracking-widest font-semibold">{label}</span></div>
      <p className="font-display font-semibold text-2xl mt-1">{value}</p>
    </div>
  );
}

/* ---------------- Enquiries ---------------- */
function EnquiriesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const empty = { month: thisMonth(), enquiries: '', enquiry_notes: '', won: '', won_notes: '' };
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const load = useCallback(() => api.get('/business/enquiries')
    .then(({ data }) => setRows(data))
    .catch((e) => { if (e.response?.status !== 402) toast.error(formatApiError(e.response?.data?.detail)); })
    .finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!f.month) { toast.error('Pick a month'); return; }
    const body = { month: f.month, enquiries: parseInt(f.enquiries, 10) || 0, enquiry_notes: f.enquiry_notes || '', won: parseInt(f.won, 10) || 0, won_notes: f.won_notes || '' };
    try {
      if (editId) await api.put(`/business/enquiries/${editId}`, body);
      else await api.post('/business/enquiries', body);
      setF(empty); setEditId(null); toast.success('Saved'); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const edit = (r) => { setEditId(r.id); setF({ month: r.month, enquiries: r.enquiries || '', enquiry_notes: r.enquiry_notes || '', won: r.won || '', won_notes: r.won_notes || '' }); };
  const del = async (id) => { if (!window.confirm('Delete this month?')) return; try { await api.delete(`/business/enquiries/${id}`); load(); } catch { toast.error('Could not delete'); } };

  const totalEnq = rows.reduce((n, r) => n + (r.enquiries || 0), 0);
  const totalWon = rows.reduce((n, r) => n + (r.won || 0), 0);
  const conv = totalEnq > 0 ? Math.round((totalWon / totalEnq) * 100) : 0;
  const chartData = [...rows].sort((a, b) => a.month.localeCompare(b.month)).map((r) => ({ month: r.month, Enquiries: r.enquiries || 0, Won: r.won || 0 }));

  return (
    <div className="space-y-6" data-testid="biz-enquiries">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total enquiries" value={totalEnq} icon={TrendingUp} />
        <StatCard label="Jobs won" value={totalWon} icon={PoundSterling} />
        <StatCard label="Conversion" value={`${conv}%`} icon={Percent} />
        <StatCard label="Months tracked" value={rows.length} icon={TrendingUp} />
      </div>

      {chartData.length > 0 && (
        <div className="tm-card p-4" data-testid="biz-enquiries-chart">
          <h3 className="font-display font-bold uppercase mb-3">Enquiries vs won</h3>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: 'monospace' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fontFamily: 'monospace' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Enquiries" fill="#0F172A" radius={[2, 2, 0, 0]} />
                <Bar dataKey="Won" fill="var(--tm-orange)" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="tm-card p-5">
        <h3 className="font-display font-bold uppercase mb-3">{editId ? 'Edit month' : 'Add / update a month'}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <label className="block"><span className="tm-label text-[10px]">Month</span>
            <input data-testid="biz-enq-month" type="month" className="tm-input" value={f.month} onChange={(e) => set('month', e.target.value)} /></label>
          <label className="block"><span className="tm-label text-[10px]">Enquiries</span>
            <input data-testid="biz-enq-count" type="number" className="tm-input" placeholder="10" value={f.enquiries} onChange={(e) => set('enquiries', e.target.value)} /></label>
          <label className="block lg:col-span-1"><span className="tm-label text-[10px]">Jobs won</span>
            <input data-testid="biz-enq-won" type="number" className="tm-input" placeholder="4" value={f.won} onChange={(e) => set('won', e.target.value)} /></label>
          <label className="block lg:col-span-2"><span className="tm-label text-[10px]">Enquiry names / notes</span>
            <input data-testid="biz-enq-notes" className="tm-input" placeholder="Beccy, Adrian, Tony…" value={f.enquiry_notes} onChange={(e) => set('enquiry_notes', e.target.value)} /></label>
          <label className="block lg:col-span-5"><span className="tm-label text-[10px]">Won names / notes</span>
            <input data-testid="biz-enq-won-notes" className="tm-input" placeholder="Beccy, Adrian, Tegala pressure wash…" value={f.won_notes} onChange={(e) => set('won_notes', e.target.value)} /></label>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button data-testid="biz-enq-save" onClick={save} className="tm-btn text-xs min-h-0 py-2"><Plus size={14} /> {editId ? 'Update' : 'Add month'}</button>
          {editId && <button onClick={() => { setEditId(null); setF(empty); }} className="tm-btn tm-btn-dark text-xs min-h-0 py-2">Cancel</button>}
        </div>
      </div>

      <div className="tm-card p-5 overflow-x-auto">
        {loading ? <p className="font-mono text-slate-500">Loading…</p> : rows.length === 0 ? (
          <p className="text-sm text-slate-400 font-mono">No months logged yet — add your first above.</p>
        ) : (
          <table className="w-full text-sm min-w-[640px]" data-testid="biz-enq-table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider font-semibold">
                <th className="py-2">Month</th><th className="text-right">Enquiries</th><th className="text-right">Won</th><th className="text-right">Conv.</th><th>Notes</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const c = r.enquiries > 0 ? Math.round((r.won / r.enquiries) * 100) : 0;
                return (
                  <tr key={r.id} className="border-b border-slate-200 align-top">
                    <td className="py-2 font-medium whitespace-nowrap">{r.month}</td>
                    <td className="text-right font-mono">{r.enquiries || 0}</td>
                    <td className="text-right font-mono font-bold text-brand">{r.won || 0}</td>
                    <td className="text-right font-mono">{c}%</td>
                    <td className="text-slate-500 text-xs max-w-[320px]">
                      {r.enquiry_notes && <div><span className="text-slate-400">Enq:</span> {r.enquiry_notes}</div>}
                      {r.won_notes && <div><span className="text-slate-400">Won:</span> {r.won_notes}</div>}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button onClick={() => edit(r)} className="text-slate-400 hover:text-slate-900 mr-2"><Pencil size={14} /></button>
                      <button data-testid={`biz-enq-delete-${r.id}`} onClick={() => del(r.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ---------------- Tools ---------------- */
const CONDITIONS = [
  { value: 'new', label: 'New' }, { value: 'good', label: 'Good' }, { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' }, { value: 'broken', label: 'Broken / retired' },
];
const CONDITION_COLOR = { new: 'bg-emerald-100 text-emerald-800', good: 'bg-emerald-50 text-emerald-700', fair: 'bg-amber-100 text-amber-800', poor: 'bg-orange-100 text-orange-800', broken: 'bg-rose-100 text-rose-800' };

function ToolsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const empty = { name: '', quantity: '1', value: '', purchase_date: '', condition: 'good', location: '' };
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const load = useCallback(() => api.get('/business/tools')
    .then(({ data }) => setRows(data))
    .catch((e) => { if (e.response?.status !== 402) toast.error(formatApiError(e.response?.data?.detail)); })
    .finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!f.name.trim()) { toast.error('Tool name required'); return; }
    const body = { name: f.name.trim(), quantity: parseInt(f.quantity, 10) || 1, value: parseFloat(f.value) || 0, purchase_date: f.purchase_date || null, condition: f.condition, location: f.location || '' };
    try {
      if (editId) await api.put(`/business/tools/${editId}`, body);
      else await api.post('/business/tools', body);
      setF(empty); setEditId(null); toast.success('Saved'); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const edit = (r) => { setEditId(r.id); setF({ name: r.name, quantity: String(r.quantity ?? 1), value: r.value || '', purchase_date: r.purchase_date || '', condition: r.condition || 'good', location: r.location || '' }); };
  const del = async (id) => { if (!window.confirm('Delete this tool?')) return; try { await api.delete(`/business/tools/${id}`); load(); } catch { toast.error('Could not delete'); } };

  const totalQty = rows.reduce((n, r) => n + (r.quantity || 0), 0);
  const totalValue = rows.reduce((n, r) => n + (r.value || 0), 0);

  return (
    <div className="space-y-6" data-testid="biz-tools">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Tool lines" value={rows.length} icon={Wrench} />
        <StatCard label="Total items" value={totalQty} icon={Wrench} />
        <StatCard label="Inventory value" value={gbp(totalValue)} icon={PoundSterling} />
      </div>

      <div className="tm-card p-5">
        <h3 className="font-display font-bold uppercase mb-3">{editId ? 'Edit tool' : 'Add a tool'}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <label className="block col-span-2"><span className="tm-label text-[10px]">Tool / equipment</span>
            <input data-testid="biz-tool-name" className="tm-input" placeholder="Makita drill" value={f.name} onChange={(e) => set('name', e.target.value)} /></label>
          <label className="block"><span className="tm-label text-[10px]">Qty</span>
            <input data-testid="biz-tool-qty" type="number" className="tm-input" placeholder="1" value={f.quantity} onChange={(e) => set('quantity', e.target.value)} /></label>
          <label className="block"><span className="tm-label text-[10px]">Value £</span>
            <input data-testid="biz-tool-value" type="number" step="any" className="tm-input" placeholder="120" value={f.value} onChange={(e) => set('value', e.target.value)} /></label>
          <label className="block"><span className="tm-label text-[10px]">Purchased</span>
            <input data-testid="biz-tool-date" type="date" className="tm-input" value={f.purchase_date} onChange={(e) => set('purchase_date', e.target.value)} /></label>
          <label className="block"><span className="tm-label text-[10px]">Condition</span>
            <select data-testid="biz-tool-condition" className="tm-input bg-white" value={f.condition} onChange={(e) => set('condition', e.target.value)}>
              {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select></label>
          <label className="block col-span-2 lg:col-span-6"><span className="tm-label text-[10px]">Location (which van / store)</span>
            <input data-testid="biz-tool-location" className="tm-input" placeholder="White Tipper" value={f.location} onChange={(e) => set('location', e.target.value)} /></label>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button data-testid="biz-tool-save" onClick={save} className="tm-btn text-xs min-h-0 py-2"><Plus size={14} /> {editId ? 'Update' : 'Add tool'}</button>
          {editId && <button onClick={() => { setEditId(null); setF(empty); }} className="tm-btn tm-btn-dark text-xs min-h-0 py-2">Cancel</button>}
        </div>
      </div>

      <div className="tm-card p-5 overflow-x-auto">
        {loading ? <p className="font-mono text-slate-500">Loading…</p> : rows.length === 0 ? (
          <p className="text-sm text-slate-400 font-mono">No tools yet — add your first above.</p>
        ) : (
          <table className="w-full text-sm min-w-[680px]" data-testid="biz-tool-table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider font-semibold">
                <th className="py-2">Tool</th><th className="text-right">Qty</th><th className="text-right">Value</th><th>Condition</th><th>Location</th><th>Purchased</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-200">
                  <td className="py-2 font-medium">{r.name}</td>
                  <td className="text-right font-mono">{r.quantity}</td>
                  <td className="text-right font-mono">{gbp(r.value)}</td>
                  <td><span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase rounded ${CONDITION_COLOR[r.condition] || ''}`}>{r.condition}</span></td>
                  <td className="text-slate-500">{r.location || '—'}</td>
                  <td className="text-slate-500 font-mono text-xs">{r.purchase_date || '—'}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => edit(r)} className="text-slate-400 hover:text-slate-900 mr-2"><Pencil size={14} /></button>
                    <button data-testid={`biz-tool-delete-${r.id}`} onClick={() => del(r.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ---------------- Vans ---------------- */
const WORK_STATUS = [
  { value: 'needs_fixing', label: 'Needs fixing', color: 'bg-rose-100 text-rose-800' },
  { value: 'booked_in', label: 'Booked in', color: 'bg-amber-100 text-amber-800' },
  { value: 'done', label: 'Done', color: 'bg-emerald-100 text-emerald-800' },
];
const statusMeta = (s) => WORK_STATUS.find((w) => w.value === s) || WORK_STATUS[2];

function VansTab() {
  const [vehicles, setVehicles] = useState([]);
  const [work, setWork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVeh, setShowVeh] = useState(false);
  const [editVeh, setEditVeh] = useState(null);

  const load = useCallback(async () => {
    try {
      const [v, w] = await Promise.all([api.get('/business/vehicles'), api.get('/business/vehicle-work')]);
      setVehicles(v.data); setWork(w.data);
    } catch (e) { if (e.response?.status !== 402) toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const totalSpend = work.reduce((n, w) => n + (w.cost || 0), 0);
  const workFor = (id) => work.filter((w) => w.vehicle_id === id);

  return (
    <div className="space-y-6" data-testid="biz-vans">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Vehicles" value={vehicles.length} icon={Truck} />
        <StatCard label="Work entries" value={work.length} icon={Wrench} />
        <StatCard label="Total overheads" value={gbp(totalSpend)} icon={PoundSterling} />
      </div>

      <button data-testid="biz-add-vehicle" onClick={() => { setEditVeh(null); setShowVeh(true); }} className="tm-btn text-xs min-h-0 py-2"><Plus size={14} /> Add vehicle</button>

      {loading ? <p className="font-mono text-slate-500">Loading…</p> : vehicles.length === 0 ? (
        <div className="tm-card p-8 text-center"><p className="font-display font-bold uppercase">No vehicles yet</p><p className="text-slate-600 text-sm mt-1">Add your first van to start logging running costs.</p></div>
      ) : vehicles.map((v) => (
        <VehicleCard key={v.id} vehicle={v} work={workFor(v.id)} onEdit={() => { setEditVeh(v); setShowVeh(true); }} onChange={load} />
      ))}

      {showVeh && <VehicleModal vehicle={editVeh} onClose={() => setShowVeh(false)} onSaved={load} />}
    </div>
  );
}

function VehicleCard({ vehicle, work, onEdit, onChange }) {
  const empty = { date: today(), description: '', cost: '', status: 'needs_fixing' };
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);
  const set = (k, val) => setF((s) => ({ ...s, [k]: val }));
  const spend = work.reduce((n, w) => n + (w.cost || 0), 0);

  const save = async () => {
    if (!f.description.trim()) { toast.error('Describe the work'); return; }
    const body = { vehicle_id: vehicle.id, date: f.date || null, description: f.description.trim(), cost: parseFloat(f.cost) || 0, status: f.status };
    try {
      if (editId) await api.put(`/business/vehicle-work/${editId}`, body);
      else await api.post('/business/vehicle-work', body);
      setF(empty); setEditId(null); toast.success('Saved'); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const editWork = (w) => { setEditId(w.id); setF({ date: w.date || today(), description: w.description || '', cost: w.cost || '', status: w.status || 'needs_fixing' }); };
  const delWork = async (id) => { try { await api.delete(`/business/vehicle-work/${id}`); onChange(); } catch { toast.error('Could not delete'); } };
  const setStatus = async (w, status) => { try { await api.put(`/business/vehicle-work/${w.id}`, { vehicle_id: vehicle.id, date: w.date, description: w.description, cost: w.cost, status }); onChange(); } catch { toast.error('Could not update'); } };

  return (
    <div className="tm-card p-5" data-testid={`biz-vehicle-${vehicle.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-xl uppercase">{vehicle.name}</h3>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            {vehicle.registration || 'No reg'}{vehicle.make_model ? ` · ${vehicle.make_model}` : ''}
          </p>
          <div className="flex flex-wrap gap-3 mt-2 text-xs font-mono">
            {vehicle.mot_due && <span className="text-slate-600">MOT due: <b>{vehicle.mot_due}</b></span>}
            {vehicle.service_due && <span className="text-slate-600">Service due: <b>{vehicle.service_due}</b></span>}
            {vehicle.tax_due && <span className="text-slate-600">Road tax due: <b>{vehicle.tax_due}</b></span>}
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Spend</p>
          <p className="font-display font-semibold text-xl text-brand">{gbp(spend)}</p>
          <button onClick={onEdit} className="text-slate-400 hover:text-slate-900 mt-1"><Pencil size={15} /></button>
        </div>
      </div>

      {work.length > 0 && (
        <div className="mt-4 divide-y divide-slate-200 border-t border-slate-200">
          {work.map((w) => {
            const m = statusMeta(w.status);
            return (
              <div key={w.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <span className="font-medium">{w.description}</span>
                  <span className="text-slate-400 font-mono text-xs"> · {w.date || '—'}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select value={w.status} onChange={(e) => setStatus(w, e.target.value)} data-testid={`biz-work-status-${w.id}`}
                    className={`text-[10px] font-mono uppercase rounded px-1.5 py-0.5 border-0 cursor-pointer ${m.color}`}>
                    {WORK_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <span className="font-mono w-16 text-right">{gbp(w.cost)}</span>
                  <button onClick={() => editWork(w)} className="text-slate-400 hover:text-slate-900"><Pencil size={13} /></button>
                  <button data-testid={`biz-work-delete-${w.id}`} onClick={() => delWork(w.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4 pt-4 border-t border-slate-200">
        <label className="block"><span className="tm-label text-[10px]">Date</span>
          <input data-testid={`biz-work-date-${vehicle.id}`} type="date" className="tm-input" value={f.date} onChange={(e) => set('date', e.target.value)} /></label>
        <label className="block col-span-2"><span className="tm-label text-[10px]">Work done</span>
          <input data-testid={`biz-work-desc-${vehicle.id}`} className="tm-input" placeholder="Front brake pads & discs" value={f.description} onChange={(e) => set('description', e.target.value)} /></label>
        <label className="block"><span className="tm-label text-[10px]">Cost £</span>
          <input data-testid={`biz-work-cost-${vehicle.id}`} type="number" step="any" className="tm-input" placeholder="180" value={f.cost} onChange={(e) => set('cost', e.target.value)} /></label>
        <label className="block"><span className="tm-label text-[10px]">Status</span>
          <select data-testid={`biz-work-newstatus-${vehicle.id}`} className="tm-input bg-white" value={f.status} onChange={(e) => set('status', e.target.value)}>
            {WORK_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select></label>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button data-testid={`biz-work-save-${vehicle.id}`} onClick={save} className="tm-btn text-xs min-h-0 py-2"><Plus size={14} /> {editId ? 'Update work' : 'Log work'}</button>
        {editId && <button onClick={() => { setEditId(null); setF(empty); }} className="tm-btn tm-btn-dark text-xs min-h-0 py-2">Cancel</button>}
      </div>
    </div>
  );
}

function VehicleModal({ vehicle, onClose, onSaved }) {
  const [f, setF] = useState({
    name: vehicle?.name || '', registration: vehicle?.registration || '', make_model: vehicle?.make_model || '',
    mot_due: vehicle?.mot_due || '', service_due: vehicle?.service_due || '', tax_due: vehicle?.tax_due || '', note: vehicle?.note || '',
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error('Vehicle name required'); return; }
    const body = { name: f.name.trim(), registration: f.registration || '', make_model: f.make_model || '', mot_due: f.mot_due || null, service_due: f.service_due || null, tax_due: f.tax_due || null, note: f.note || '' };
    try {
      if (vehicle) await api.put(`/business/vehicles/${vehicle.id}`, body);
      else await api.post('/business/vehicles', body);
      toast.success('Saved'); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async () => {
    if (!vehicle || !window.confirm('Delete this vehicle and all its work log?')) return;
    try { await api.delete(`/business/vehicles/${vehicle.id}`); onSaved(); onClose(); } catch { toast.error('Could not delete'); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="tm-card bg-white p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="biz-vehicle-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-xl uppercase">{vehicle ? 'Edit vehicle' : 'Add vehicle'}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-2"><span className="tm-label text-[10px]">Name</span>
            <input data-testid="biz-veh-name" className="tm-input" placeholder="White Tipper" value={f.name} onChange={(e) => set('name', e.target.value)} /></label>
          <label className="block"><span className="tm-label text-[10px]">Registration</span>
            <input data-testid="biz-veh-reg" className="tm-input" placeholder="AB12 CDE" value={f.registration} onChange={(e) => set('registration', e.target.value)} /></label>
          <label className="block"><span className="tm-label text-[10px]">Make / model</span>
            <input className="tm-input" placeholder="Ford Transit" value={f.make_model} onChange={(e) => set('make_model', e.target.value)} /></label>
          <label className="block"><span className="tm-label text-[10px]">MOT due</span>
            <input data-testid="biz-veh-mot" type="date" className="tm-input" value={f.mot_due} onChange={(e) => set('mot_due', e.target.value)} /></label>
          <label className="block"><span className="tm-label text-[10px]">Service due</span>
            <input data-testid="biz-veh-service" type="date" className="tm-input" value={f.service_due} onChange={(e) => set('service_due', e.target.value)} /></label>
          <label className="block col-span-2"><span className="tm-label text-[10px]">Road tax (VED) due</span>
            <input data-testid="biz-veh-tax" type="date" className="tm-input" value={f.tax_due} onChange={(e) => set('tax_due', e.target.value)} /></label>
        </div>
        <div className="flex items-center justify-between mt-4">
          <button data-testid="biz-veh-save" onClick={save} className="tm-btn text-xs min-h-0 py-2"><Plus size={14} /> {vehicle ? 'Save changes' : 'Add vehicle'}</button>
          {vehicle && <button data-testid="biz-veh-delete" onClick={del} className="text-rose-600 hover:text-rose-800 text-xs uppercase flex items-center gap-1 font-semibold"><Trash2 size={14} /> Delete</button>}
        </div>
      </div>
    </div>
  );
}


/* ---------------- Business tax dates ---------------- */
const yearAhead = (mm, dd) => {
  const now = new Date();
  let y = now.getFullYear();
  const target = new Date(`${y}-${mm}-${dd}T00:00:00`);
  if (target < now) y += 1;
  return `${y}-${mm}-${dd}`;
};
const TAX_PRESETS = [
  { label: 'Self Assessment — file & pay', date: () => yearAhead('01', '31') },
  { label: 'Payment on account (2nd)', date: () => yearAhead('07', '31') },
  { label: 'VAT return', date: () => today() },
];

function TaxDatesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const empty = { label: '', due_date: '', note: '' };
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const load = useCallback(() => api.get('/business/tax-dates')
    .then(({ data }) => setRows(data))
    .catch((e) => { if (e.response?.status !== 402) toast.error(formatApiError(e.response?.data?.detail)); })
    .finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!f.label.trim()) { toast.error('Add a label'); return; }
    if (!f.due_date) { toast.error('Pick a due date'); return; }
    const body = { label: f.label.trim(), due_date: f.due_date, note: f.note || '' };
    try {
      if (editId) await api.put(`/business/tax-dates/${editId}`, body);
      else await api.post('/business/tax-dates', body);
      setF(empty); setEditId(null); toast.success('Saved'); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const edit = (r) => { setEditId(r.id); setF({ label: r.label, due_date: r.due_date, note: r.note || '' }); };
  const del = async (id) => { if (!window.confirm('Delete this tax date?')) return; try { await api.delete(`/business/tax-dates/${id}`); load(); } catch { toast.error('Could not delete'); } };
  const quickAdd = (p) => setF({ label: p.label, due_date: p.date(), note: '' });

  return (
    <div className="space-y-6" data-testid="biz-tax">
      <div className="tm-card p-5">
        <h3 className="font-display font-bold uppercase mb-1">{editId ? 'Edit tax date' : 'Add a tax date'}</h3>
        <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-3 font-semibold">Add your key HMRC / VAT deadlines — you'll get a banner &amp; email when they're within 30 days</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {TAX_PRESETS.map((p) => (
            <button key={p.label} data-testid={`biz-tax-preset-${p.label}`} onClick={() => quickAdd(p)} className="tm-btn tm-btn-dark text-[11px] min-h-0 py-1.5">
              <Plus size={12} /> {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label className="block sm:col-span-1"><span className="tm-label text-[10px]">What's due</span>
            <input data-testid="biz-tax-label" className="tm-input" placeholder="Self Assessment" value={f.label} onChange={(e) => set('label', e.target.value)} /></label>
          <label className="block"><span className="tm-label text-[10px]">Due date</span>
            <input data-testid="biz-tax-date" type="date" className="tm-input" value={f.due_date} onChange={(e) => set('due_date', e.target.value)} /></label>
          <label className="block"><span className="tm-label text-[10px]">Note (optional)</span>
            <input className="tm-input" placeholder="Set aside 25%" value={f.note} onChange={(e) => set('note', e.target.value)} /></label>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button data-testid="biz-tax-save" onClick={save} className="tm-btn text-xs min-h-0 py-2"><Plus size={14} /> {editId ? 'Update' : 'Add date'}</button>
          {editId && <button onClick={() => { setEditId(null); setF(empty); }} className="tm-btn tm-btn-dark text-xs min-h-0 py-2">Cancel</button>}
        </div>
      </div>

      <div className="tm-card p-5">
        {loading ? <p className="font-mono text-slate-500">Loading…</p> : rows.length === 0 ? (
          <p className="text-sm text-slate-400 font-mono">No tax dates yet — add your HMRC/VAT deadlines above.</p>
        ) : (
          <div className="divide-y divide-slate-200" data-testid="biz-tax-list">
            {rows.map((r) => {
              const days = Math.round((new Date(r.due_date + 'T00:00:00') - new Date()) / 86400000);
              const soon = days <= 30;
              return (
                <div key={r.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <span className="font-medium">{r.label}</span>
                    {r.note && <span className="text-slate-400"> · {r.note}</span>}
                    <span className="text-slate-400 font-mono text-xs block sm:inline sm:ml-2">{r.due_date}{soon ? ` · ${days < 0 ? `${Math.abs(days)}d overdue` : `in ${days}d`}` : ''}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {soon && <span className={`px-2 py-0.5 text-[10px] font-mono uppercase rounded ${days < 7 ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'}`}>Due soon</span>}
                    <button onClick={() => edit(r)} className="text-slate-400 hover:text-slate-900"><Pencil size={14} /></button>
                    <button data-testid={`biz-tax-delete-${r.id}`} onClick={() => del(r.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Calendar ---------------- */
const EVENT_TYPES = [
  { value: 'job', label: 'Job', color: '#0F172A' },
  { value: 'site_visit', label: 'Site visit', color: '#0EA5E9' },
  { value: 'quote', label: 'Scheduled quote', color: 'var(--tm-orange)' },
  { value: 'appointment', label: 'Appointment', color: '#7C3AED' },
];
const typeMeta = (t) => EVENT_TYPES.find((e) => e.value === t) || EVENT_TYPES[3];
const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const fmtTime = (iso) => (iso && iso.length > 10 ? iso.slice(11, 16) : '');
const fmtDay = (iso) => new Date(iso.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

function CalendarTab() {
  const [events, setEvents] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [modal, setModal] = useState(null); // {event} or {date}

  const load = useCallback(() => {
    api.get('/calendar/events').then(({ data }) => setEvents(data)).catch((e) => { if (e.response?.status !== 402) toast.error(formatApiError(e.response?.data?.detail)); });
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/jobs').then(({ data }) => setJobs(data)).catch(() => {});
    api.get('/projects').then(({ data }) => setQuotes(data)).catch(() => {});
  }, []);

  const byDay = {};
  events.forEach((e) => { const k = e.start?.slice(0, 10); if (k) (byDay[k] = byDay[k] || []).push(e); });

  const first = new Date(cursor.y, cursor.m, 1);
  const startBlanks = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const monthLabel = first.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const cells = [...Array(startBlanks).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const move = (d) => setCursor((c) => { const nd = new Date(c.y, c.m + d, 1); return { y: nd.getFullYear(), m: nd.getMonth() }; });
  const todayStr = today();

  const upcoming = [...events].filter((e) => (e.start || '').slice(0, 10) >= todayStr).sort((a, b) => a.start.localeCompare(b.start)).slice(0, 12);

  return (
    <div className="space-y-6" data-testid="biz-calendar">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={() => move(-1)} className="tm-btn tm-btn-dark min-h-0 p-2"><ChevronLeft size={16} /></button>
          <h3 className="font-display font-semibold text-xl uppercase w-52 text-center">{monthLabel}</h3>
          <button data-testid="cal-next" onClick={() => move(1)} className="tm-btn tm-btn-dark min-h-0 p-2"><ChevronRight size={16} /></button>
          <button onClick={() => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); }} className="tm-btn tm-btn-dark text-xs min-h-0 py-2 ml-1">Today</button>
        </div>
        <button data-testid="cal-add-event" onClick={() => setModal({ date: todayStr })} className="tm-btn text-xs min-h-0 py-2"><Plus size={14} /> Add event</button>
      </div>

      <div className="tm-card p-3 sm:p-4 overflow-x-auto">
        <div className="grid grid-cols-7 gap-1 min-w-[640px]">
          {WD.map((d) => <div key={d} className="text-[10px] uppercase tracking-widest text-slate-400 text-center py-1 font-semibold">{d}</div>)}
          {cells.map((day, idx) => {
            if (!day) return <div key={`b${idx}`} className="min-h-[92px]" />;
            const ds = `${cursor.y}-${String(cursor.m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const evs = byDay[ds] || [];
            const isToday = ds === todayStr;
            return (
              <button key={ds} data-testid={`cal-day-${ds}`} onClick={() => setModal({ date: ds })}
                className={`min-h-[92px] text-left p-1.5 border transition-colors hover:border-brand ${isToday ? 'border-brand bg-orange-50' : 'border-slate-200'}`}>
                <span className={`font-mono text-xs ${isToday ? 'font-semibold text-brand' : 'text-slate-500'}`}>{day}</span>
                <div className="space-y-0.5 mt-0.5">
                  {evs.slice(0, 3).map((e) => {
                    const m = typeMeta(e.type);
                    return (
                      <div key={e.id} data-testid={`cal-event-${e.id}`} onClick={(ev) => { ev.stopPropagation(); setModal({ event: e }); }}
                        className="text-[10px] leading-tight text-white px-1 py-0.5 truncate cursor-pointer" style={{ background: m.color }} title={e.title}>
                        {fmtTime(e.start) && <b className="font-mono">{fmtTime(e.start)} </b>}{e.title}
                      </div>
                    );
                  })}
                  {evs.length > 3 && <div className="text-[10px] text-slate-400 font-mono">+{evs.length - 3} more</div>}
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-200">
          {EVENT_TYPES.map((t) => <span key={t.value} className="flex items-center gap-1.5 text-[10px] uppercase text-slate-500 font-semibold"><span className="w-3 h-3 inline-block" style={{ background: t.color }} />{t.label}</span>)}
        </div>
      </div>

      <div className="tm-card p-5">
        <h3 className="font-display font-bold uppercase mb-3">Upcoming</h3>
        {upcoming.length === 0 ? (
          <p className="text-sm text-slate-400 font-mono">Nothing scheduled yet — click a day or “Add event”.</p>
        ) : (
          <div className="divide-y divide-slate-200" data-testid="cal-upcoming">
            {upcoming.map((e) => {
              const m = typeMeta(e.type);
              const job = jobs.find((j) => j.id === e.job_id);
              const quote = quotes.find((q) => q.id === e.quote_id);
              return (
                <button key={e.id} data-testid={`cal-upcoming-${e.id}`} onClick={() => setModal({ event: e })} className="w-full text-left py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors">
                  <span className="w-1.5 self-stretch shrink-0" style={{ background: m.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold uppercase text-sm">{e.title}</span>
                      <span className="tm-chip text-[10px]" style={{ background: m.color, color: '#fff' }}>{m.label}</span>
                      {(e.remind_day_before || e.remind_2h_before) && <Bell size={12} className="text-brand" />}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5 text-xs text-slate-500 font-mono">
                      <span className="flex items-center gap-1"><Clock size={11} />{fmtDay(e.start)}{fmtTime(e.start) ? ` · ${fmtTime(e.start)}` : ' · all day'}</span>
                      {e.customer_name && <span>{e.customer_name}</span>}
                      {e.location && <span className="flex items-center gap-1"><MapPin size={11} />{e.location}</span>}
                      {job && <span className="text-slate-400">Job: {job.name}</span>}
                      {quote && <span className="text-slate-400">Quote: {quote.name}</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {modal && <EventModal seed={modal} jobs={jobs} quotes={quotes} onClose={() => setModal(null)} onSaved={load} />}
    </div>
  );
}

function EventModal({ seed, jobs, quotes, onClose, onSaved }) {
  const ev = seed.event;
  const initStart = ev?.start || `${seed.date}T09:00`;
  const [f, setF] = useState({
    title: ev?.title || '', type: ev?.type || 'appointment',
    date: initStart.slice(0, 10), time: (initStart.length > 10 ? initStart.slice(11, 16) : '09:00'),
    end_date: ev?.end?.slice(0, 10) || '', end_time: (ev?.end && ev.end.length > 10 ? ev.end.slice(11, 16) : ''),
    all_day: ev?.all_day || false, location: ev?.location || '', customer_name: ev?.customer_name || '', note: ev?.note || '',
    job_id: ev?.job_id || '', quote_id: ev?.quote_id || '',
    remind_day_before: ev?.remind_day_before ?? true, remind_2h_before: ev?.remind_2h_before ?? true,
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.title.trim()) { toast.error('Add a title'); return; }
    if (!f.date) { toast.error('Pick a date'); return; }
    const start = f.all_day ? f.date : `${f.date}T${f.time || '09:00'}`;
    let end = null;
    if (f.end_date) end = f.all_day ? f.end_date : `${f.end_date}T${f.end_time || f.time || '09:00'}`;
    const body = {
      title: f.title.trim(), type: f.type, start, end, all_day: f.all_day,
      location: f.location || '', customer_name: f.customer_name || '', note: f.note || '',
      job_id: f.job_id || null, quote_id: f.quote_id || null,
      remind_day_before: f.remind_day_before, remind_2h_before: f.remind_2h_before,
    };
    try {
      if (ev) await api.put(`/calendar/events/${ev.id}`, body);
      else await api.post('/calendar/events', body);
      toast.success('Saved'); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async () => {
    if (!ev || !window.confirm('Delete this event?')) return;
    try { await api.delete(`/calendar/events/${ev.id}`); onSaved(); onClose(); } catch { toast.error('Could not delete'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="tm-card bg-white p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cal-event-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-xl uppercase">{ev ? 'Edit event' : 'New event'}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <label className="block"><span className="tm-label text-[10px]">Title</span>
            <input data-testid="cal-title" className="tm-input" placeholder="Quote — Mrs Beccy patio" value={f.title} onChange={(e) => set('title', e.target.value)} /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="tm-label text-[10px]">Type</span>
              <select data-testid="cal-type" className="tm-input bg-white" value={f.type} onChange={(e) => set('type', e.target.value)}>
                {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select></label>
            <label className="flex items-end gap-2 pb-2"><input data-testid="cal-allday" type="checkbox" checked={f.all_day} onChange={(e) => set('all_day', e.target.checked)} /><span className="text-sm">All day</span></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="tm-label text-[10px]">Start date</span>
              <input data-testid="cal-start-date" type="date" className="tm-input" value={f.date} onChange={(e) => set('date', e.target.value)} /></label>
            {!f.all_day && <label className="block"><span className="tm-label text-[10px]">Start time</span>
              <input data-testid="cal-start-time" type="time" className="tm-input" value={f.time} onChange={(e) => set('time', e.target.value)} /></label>}
            <label className="block"><span className="tm-label text-[10px]">End date (optional)</span>
              <input data-testid="cal-end-date" type="date" min={f.date} className="tm-input" value={f.end_date} onChange={(e) => set('end_date', e.target.value)} /></label>
            {!f.all_day && <label className="block"><span className="tm-label text-[10px]">End time</span>
              <input data-testid="cal-end-time" type="time" className="tm-input" value={f.end_time} onChange={(e) => set('end_time', e.target.value)} /></label>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="tm-label text-[10px]">Customer</span>
              <input data-testid="cal-customer" className="tm-input" placeholder="Beccy" value={f.customer_name} onChange={(e) => set('customer_name', e.target.value)} /></label>
            <label className="block"><span className="tm-label text-[10px]">Location</span>
              <input data-testid="cal-location" className="tm-input" placeholder="12 High St" value={f.location} onChange={(e) => set('location', e.target.value)} /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="tm-label text-[10px]">Link a job</span>
              <select data-testid="cal-job" className="tm-input bg-white" value={f.job_id} onChange={(e) => set('job_id', e.target.value)}>
                <option value="">— none —</option>
                {jobs.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
              </select></label>
            <label className="block"><span className="tm-label text-[10px]">Link a quote</span>
              <select data-testid="cal-quote" className="tm-input bg-white" value={f.quote_id} onChange={(e) => set('quote_id', e.target.value)}>
                <option value="">— none —</option>
                {quotes.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
              </select></label>
          </div>
          <label className="block"><span className="tm-label text-[10px]">Notes</span>
            <input className="tm-input" placeholder="Bring measuring wheel" value={f.note} onChange={(e) => set('note', e.target.value)} /></label>
          <div className="border-t border-slate-200 pt-3">
            <p className="tm-label text-[10px] mb-1">Email reminders</p>
            <label className="flex items-center gap-2 text-sm py-1"><input data-testid="cal-remind-day" type="checkbox" checked={f.remind_day_before} onChange={(e) => set('remind_day_before', e.target.checked)} /> The day before (6pm)</label>
            <label className="flex items-center gap-2 text-sm py-1"><input data-testid="cal-remind-2h" type="checkbox" checked={f.remind_2h_before} onChange={(e) => set('remind_2h_before', e.target.checked)} disabled={f.all_day} /> 2 hours before {f.all_day && <span className="text-slate-400 text-xs">(timed events only)</span>}</label>
          </div>
        </div>
        <div className="flex items-center justify-between mt-5">
          <button data-testid="cal-save" onClick={save} className="tm-btn text-xs min-h-0 py-2"><Plus size={14} /> {ev ? 'Save changes' : 'Add event'}</button>
          {ev && <button data-testid="cal-delete" onClick={del} className="text-rose-600 hover:text-rose-800 text-xs uppercase flex items-center gap-1 font-semibold"><Trash2 size={14} /> Delete</button>}
        </div>
      </div>
    </div>
  );
}

